import classnames from 'classnames';
import moment from 'moment';
import * as PropTypes from 'prop-types';
import { toUnicode } from 'punycode';
import React from 'react';
import DownloadCSV from '../../../../components/DownloadCSV';
import { HoverableTD } from '../../../../components/HoverableTD';
import NoDataToDisplay from '../../../../components/NoDataToDisplay';
import PopoutWindow from '../../../../components/PopoutWindow';
import RangedDtPicker from '../../../../components/RangedDtPicker';
import Wait4Me from '../../../../components/Wait4Me';
import { getCustomerAlertHistory } from '../../../../services/tm/investigationDetail';
import Table from '../../dashboard/TookiTakiTable/Table';
import style from './index.scss';
import TableHeader from '../../../../components/TableHeader';
import { getAlertLevelRow } from '../../../../components/TableHeader/tableRowUtils';
import { getAlertIDFromURL, putHyphenIfEmpty } from '../../../../services/util';
import { ConfigData, getABData } from '../../../../util/ABUtil';
import { convertDateInputToEpoch } from '../../../../util/timezone';

/**
 * TransactionRow for single row
 * @param txt
 * @returns {*}
 * @constructor
 */
const TransactionRow = ({ txt }) => {
  return (
    <div className="row" style={{ textAlign: 'center', padding: 10 }}>
      <div className="col-md-12">
        <p style={{ textAlign: 'center' }}>{txt}</p>
      </div>
    </div>
  );
};
/**
 * AlertHistoryRow header
 */
const AlertHistoryRow = [
  {
    label: getABData('Alert/Anomaly ID'),
    name: getABData('Alert/Anomaly ID'),
    key: 'alertId'
  },
  {
    label: getABData('Alert/Anomaly Date'),
    name: getABData('Alert/Anomaly Date'),
    key: 'alertDate'
  },
  {
    label: 'Alert Type',
    name: 'Alert Type',
    key: 'alertType'
  },
  {
    label: getABData('Alert/Anomaly Score'),
    name: getABData('Alert/Anomaly Score'),
    key: 'alertScore'
  },
  {
    label: 'Account Number',
    name: 'Account Number',
    key: 'accountId'
  },
  {
    label: 'Account Currency',
    name: 'Account Currency',
    key: 'accountCurrency'
  },
  {
    label: 'Business Segment',
    name: 'Business Segment',
    key: 'segmentName'
  },
  {
    label: 'Analyst Name',
    name: 'Analyst Name',
    key: 'analystName'
  },
  {
    label: 'Rules Triggered/Featured Names',
    name: 'Rules Triggered/Featured Names',
    key: 'rules'
  },
  {
    label: getABData('Alert/Anomaly Status'),
    name: getABData('Alert/Anomaly Status'),
    key: 'alertStatus'
  },
  {
    label: getABData('Alert/Anomaly Close Date'),
    name: getABData('Alert/Anomaly Close Date'),
    key: 'closureDate'
  },
  {
    label: 'STR',
    name: 'STR',
    key: 'isStr'
  },
  {
    label: 'Outcome',
    name: 'Outcome',
    key: 'investigationResult'
  }
];
/**
 * AlertFilter for filtering
 * @param activeOption
 * @param onFilterClick
 * @returns {*}
 * @constructor
 */
const AlertFilter = ({ activeOption = 0, onFilterClick }) => {
  if (!ConfigData.IS_NCD_NOT_AVAILABLE)
    return (
      <div className={style.table_selection_list}>
        <a
          onClick={() => {
            onFilterClick(0);
          }}
          className={classnames(activeOption === 0 ? style.active : '')}>
          All
        </a>
        <a
          onClick={() => {
            onFilterClick(1);
          }}
          className={classnames(
            activeOption === 1 ? style.active : '',
            style.indicator,
            style.alert
          )}>
          Alerts
        </a>

        <a
          onClick={() => {
            onFilterClick(2);
          }}
          className={classnames(
            activeOption === 2 ? style.active : '',
            style.indicator,
            style.anomaly
          )}>
          Anomaly
        </a>
      </div>
    );
  else return null;
};

/**
 * AlertTableComponent
 * @param data
 * @param keys
 * @returns {*}
 * @constructor
 */
const AlertTableComponent = ({ data, keys }) => (
  <Table isStriped={true}>
    <TableHeader data={keys} />
    <tbody>
      {data.map((tableRow) => {
        return (
          <tr>
            {keys.map((row) => {
              const firstDataPoint = putHyphenIfEmpty(tableRow[row.key]);
              if (row.key === 'alertScore') {
                return getAlertLevelRow(tableRow, row);
              }
              return <HoverableTD row={row} firstDataPoint={firstDataPoint} />;
            })}
          </tr>
        );
      })}
    </tbody>
  </Table>
);

/**
 * HistAlert main component
 */
class HistAlert extends React.Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    const { histAlert } = nextProps;

    const data = histAlert?.alert_list;
    const count = histAlert?.alert_list?.length || 0;

    const newHistAlerts = Array.isArray(data) ? data : [];

    return {
      alertsList: newHistAlerts,
      filteredAlertsList: prevState.filterClicked
        ? prevState.filteredAlertsList
        : newHistAlerts,
      hasMore: newHistAlerts.length < count
    };
  }

  // eslint-disable-line react/prefer-stateless-function
  constructor(props) {
    super(props);
    this.state = {
      newDataFlag: true,
      alertsList: [], // hist alerts in the table
      filteredAlertsList: [], // hist alerts in the table
      hasMore: true,
      filterClicked: false,
      currentPage: 0,
      histData: [],
      filterApplied: false,
      tableWidth: '100%',
      dateRange: {
        startDate: moment().subtract(30, 'days').add(1, 'days'),
        endDate: moment(),
        activeRangeNo: 2
      },
      //actual filter information passed in by the filter component
      combinedFilters: {
        strs: null,
        results: null,
        customers: null
      }
    };

    this.dataUrlMap = {};
  }

  updateTableWidth = () => {
    const elem = document.getElementById('histTransactionContent');
    if (elem) {
      const computedStyle = window.getComputedStyle(elem, null);
      const newWidth = computedStyle.getPropertyValue('width');
      this.setState({
        tableWidth: parseInt(newWidth)
      });
    }
  };

  componentDidMount() {
    window.addEventListener('resize', this.updateTableWidth);
    this.props.onDateRangeChanged(
      this.state.dateRange.startDate,
      this.state.dateRange.endDate
    );
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateTableWidth);
  }

  getFileName = () => {
    const dateObj = new Date();
    const timestamp = dateObj.getTime();
    const { alertId } = this.props;
    return `AlertHistory__${alertId}--${this.state.dateRange.startDate.format(
      'DDMMYYYY'
    )}-to-${this.state.dateRange.endDate.format('DDMMYYYY')}__${timestamp}.csv`;
  };

  onAlertHistDownload = async () => {
    this.setState({ histData: [] });
    const { alertsList } = this.state;
    let customerID = '';
    if (alertsList.length > 0) {
      customerID = alertsList[0].primaryPartyKey;
    } else {
      this.setState({ histData: [{}] });
      return false;
    }
    const { alertID } = getAlertIDFromURL();
    const {
      dateRange: { startDate, endDate }
    } = this.state;
    const alertHistData = await getCustomerAlertHistory({
      customerId: customerID,
      alertId: alertID,
      from: convertDateInputToEpoch(startDate, true),
      to: convertDateInputToEpoch(endDate, true),
      offset: 0,
      limit: 5000
    });
    const { alert_list } = alertHistData;
    this.setState({ histData: alert_list });
  };

  onChangeWidgetAction = (widgetActionObject) => {
    const { from, to, q } = widgetActionObject;

    const newDateRange = {
      startDate: from,
      endDate: to
    };
    this.setState({
      ...this.state,
      dateRange: newDateRange,
      newDataFlag: true,
      hasMore: true,
      currentPage: 0,
      filterClicked: false,
      activeOption: 0,
      ruleValue: {
        id: 0,
        name: '--Rule Unspecified--'
      },
      combinedFilters: {
        strs: null,
        results: null,
        customers: null
      },
      q
    });

    this.props.onDateRangeChanged(from, to, q);
  };

  onFilterClick = (e) => {
    let filteredList = this.state.alertsList;
    filteredList = filteredList.filter((alert) => {
      if (e === 1) {
        return alert.alertType === 'ALERT';
      } else if (e === 2) {
        return alert.alertType === 'ANOMALY';
      } else {
        return alert;
      }
    });
    this.setState(
      {
        filterClicked: true,
        activeOption: e,
        filteredAlertsList: filteredList
      },
      () => {
        console.log(this.state.filteredAlertsList);
      }
    );
  };

  onRulesDropdownChange = (value) => {
    this.setState({
      ruleValue: value,
      hasMore: true
    });

    let {
      dateRange: { startDate, endDate },
      combinedFilters: { strs, customers, results }
    } = this.state;
    strs = strs || {};
    results = results || {};
    customers = customers || {};

    const filteredCustomers = Object.keys(customers).filter(
      (ck) => customers[ck]
    );
    const customersFilter =
      filteredCustomers.length > 0
        ? filteredCustomers.reduce((accum, id, idx) => accum + ',' + id)
        : null;

    const strsFilter =
      Object.keys(strs).filter((x) => !strs[x]).length > 0
        ? strs['TRUE']
          ? 'TRUE'
          : 'FALSE'
        : null;
    const resultsFilter =
      Object.keys(results).filter((x) => !results[x]).length > 0
        ? results['issue']
          ? 'ISSUE'
          : 'NO_ISSUE'
        : null;
    const ruleFilter = value.id == 0 ? null : value.id;

    this.props.onFilterApplied(
      startDate,
      endDate,
      strsFilter,
      resultsFilter,
      customersFilter,
      ruleFilter
    );
  };

  onScrollToLoad = () => {
    const {
      histAlert: { hasMore = false }
    } = this.props;
    if (hasMore) {
      let currentPage = this.state.currentPage;
      currentPage += 1;

      this.setState({
        ...this.state,
        newDataFlag: false,
        currentPage
      });

      let {
        dateRange: { startDate, endDate }
      } = this.state;
      if (this.props.onScrollToLoad) {
        this.props.onScrollToLoad(startDate, endDate, currentPage * 10, q);
      }
    }
  };

  onDateChange = (start, end, rangeNo) => {
    const newDateRange = {
      startDate: start,
      endDate: end,
      activeRangeNo: rangeNo
    };
    this.setState({
      ...this.state,
      dateRange: newDateRange,
      newDataFlag: true,
      hasMore: true,
      currentPage: 0,
      filterClicked: false,
      activeOption: 0,
      ruleValue: {
        id: 0,
        name: '--Rule Unspecified--'
      },
      combinedFilters: {
        strs: null,
        results: null,
        customers: null
      }
    });

    this.props.onDateRangeChanged(start, end);
  };

  getFileName = () => {
    return `${this.state.dateRange.startDate.format(
      'DDMMYYYY'
    )}-to-${this.state.dateRange.endDate.format('DDMMYYYY')}.csv`;
  };

  applyFilter = (filterValue) => {
    let {
      combinedFilterOptions: { strs, results, customers },
      payloadFilters
    } = filterValue;
    this.setState({
      filterApplied: true,
      filterActive: false,
      newDataFlag: true,
      hasMore: toUnicode,
      currentPage: 0,
      combinedFilters: {
        strs,
        results,
        customers
      }
    });

    const {
      dateRange: { startDate, endDate }
    } = this.state;

    this.props.onFilterApplied({
      from: startDate.format('DD/MM/YYYY'),
      to: endDate.format('DD/MM/YYYY'),
      q: q || btoa('{}'),
      ...payloadFilters
    });
  };

  cancelFilter = () => {
    this.setState({
      filterApplied: false,
      filterActive: false
    });
  };

  toggleFilter = () => {
    this.setState({
      filterActive: !this.state.filterActive
    });
  };

  togglePopout = () => {
    ['filter', 'filterChecked'].forEach((title) => {
      let querySelector = `img[title='${title}']`;
      let image = document.querySelector(querySelector);
      if (image) {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        canvas.getContext('2d').drawImage(image, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        this.dataUrlMap[title] = dataUrl;
      }
    });
    this.setState({
      isPopedOut: !this.state.isPopedOut
    });
  };

  updateTableWidth = () => {
    const elem = document.getElementById('histTransactionContent');
    if (elem) {
      const computedStyle = window.getComputedStyle(elem, null);
      const newWidth = computedStyle.getPropertyValue('width');
      this.setState({
        tableWidth: parseInt(newWidth)
      });
    }
  };

  render() {
    const { isPopedOut, dateRange } = this.state;
    const {
      histAlert: { data, hasMore = false, isFetching },
      isLoading,
      settings
    } = this.props;

    let { rules } = data?.metadata || {};
    rules = rules || [];
    if (rules.filter((x) => x.id === 0).length === 0) {
      rules.push({
        id: 0,
        name: '--Rule Unspecified--'
      });
    }

    let alertsList = this.state.filteredAlertsList;
    let sectionHeader = (
      <div
        className={classnames('row', style.primaryTableHead, style.marTop15)}>
        <AlertFilter
          activeOption={this.state.activeOption}
          onFilterClick={this.onFilterClick}
        />

        <div className={style.dateSection}>
          <RangedDtPicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            activeRangeNo={dateRange.activeRangeNo}
            onDateChange={this.onDateChange}
          />

          {/*Download Option Start */}
          <DownloadCSV
            csvData={this.state.histData}
            csvHeaders={AlertHistoryRow}
            fileName={this.getFileName()}
            onDownloadClick={this.onAlertHistDownload}
          />
          {/*Download Option End */}
        </div>
      </div>
    );

    //table content
    let paginatedRowOption = [];
    if (alertsList && alertsList.length > 0) {
      alertsList.forEach((row, i) => {
        paginatedRowOption.push(<tr>{[]}</tr>);
      });
      if (hasMore) {
        if (!isLoading) {
          paginatedRowOption.push(
            <div className="row" style={{ textAlign: 'center', padding: 10 }}>
              <div className="col-md-12">
                <a onClick={this.onScrollToLoad} className={style.loadMore}>
                  Load More
                </a>
              </div>
            </div>
          );
        } else {
          paginatedRowOption.push(<TransactionRow txt={'Loading...'} />);
        }
      }
    } else if (isFetching || isLoading === undefined) {
      paginatedRowOption.push(
        <div className="row" style={{ textAlign: 'center', padding: 10 }}>
          <div className="col-md-12">
            <Wait4Me />
          </div>
        </div>
      );
    } else {
      paginatedRowOption.push(<NoDataToDisplay />);
    }

    let renderedView = (
      <div className={classnames(style.card, 'mt-3')}>
        <div className={classnames(style.sectionHeader)}>
          <span className={style.assocTitle}>All Alerts</span>
          {sectionHeader}
        </div>
        <div></div>
        <div className={classnames(style.card_body)}>
          <AlertTableComponent keys={AlertHistoryRow} data={alertsList} />
          {paginatedRowOption}
        </div>
      </div>
    );

    if (!isPopedOut) {
      return renderedView;
    }

    return (
      <div>
        <PopoutWindow
          width={window.innerWidth}
          height={500}
          imageUrlMap={this.dataUrlMap}>
          {renderedView}
        </PopoutWindow>
      </div>
    );
  }
}

HistAlert.propTypes = {
  alertId: PropTypes.string,
  customerId: PropTypes.string,
  GSConfig: PropTypes.object,
  alertHistoryData: PropTypes.object,
  onScrollToLoad: PropTypes.func,
  onDateRangeChanged: PropTypes.func
};

export default HistAlert;
